import { useFormik } from 'formik'
import { useRouter } from 'next/router'
/* eslint-disable tailwindcss/no-custom-classname */
import { useCallback, useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useIMask } from 'react-imask'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import Icon from '@/components/Icon'
import { useTonLead } from '@/hooks/useTonLead'
import tonLeadService from '@/services/tonLead'
import cn from '@/utils/cn'

import ErrorMessage from './ErrorMessage'
import { schema } from './schema'
import styles from './style.module.css'

export default function OpenAccountForm() {
  const router = useRouter()
  const { updateLead } = useTonLead()
  const [showErrorMessage, setShowErrorMessage] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [loadingEmail, setLoadingCheckEmail] = useState<boolean>(false)

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      return
    }
  }, [executeRecaptcha])

  useEffect(() => {
    handleReCaptchaVerify()
  }, [handleReCaptchaVerify])

  const checkEmail = async (email: string) => {
    setLoadingCheckEmail(true)
    if (!executeRecaptcha) return
    const emailExists = await tonLeadService.checkExists(email)

    if (emailExists.status === 200) {
      handleReCaptchaVerify()
      setLoadingCheckEmail(false)
      return false
    } else if (emailExists.status === 404) {
      return true
    }
  }

  const formik = useFormik({
    validateOnMount: true,
    validationSchema: toFormikValidationSchema(schema),
    initialValues: {
      name: '',
      email: '',
      phone: ''
    },
    onSubmit: async (values) => {
      const isEmailValid = await checkEmail(values.email)

      if (!isEmailValid) {
        formik.setFieldError('email', 'Esse email já está sendo usado.')
        const inputEmail = document.getElementById(
          'input-email'
        ) as HTMLInputElement
        inputEmail.value = '#DF0C39'
        return
      }

      const queryStrings = new URLSearchParams(window.location.search)

      queryStrings.set('utm_placement', 'tap_ton')

      const utm: Record<string, string> = {}

      queryStrings.forEach((value, key) => {
        if (!key.startsWith('utm_')) return

        const partialKey = key.slice(4)

        utm[partialKey] = value
      })

      updateLead({
        ...values,
        utm
      })
        .then(() => {
          localStorage.setItem(
            'landing-new-account-form',
            JSON.stringify(values)
          )
          setLoadingCheckEmail(false)
          router.push(`/checkout/new-account/personal-info?${queryStrings}`)
        })
        .catch(() => {
          setShowErrorMessage(true)
        })
    }
  })

  const { ref: inputPhoneRef } = useIMask<HTMLInputElement>(
    {
      mask: '(00) 90000-0000'
    },
    {
      onAccept: (value) => {
        formik.setFieldValue('phone', value)
      }
    }
  )

  return (
    <div id="open-account-form">
      <section className="mx-auto mt-24 max-w-[1200px] text-display-900 bg-white rounded-24">
        <h3 className="mb-24 font-heading text-center ton-heading-2 md:ton-heading-3">
          Abra sua conta grátis
        </h3>

        {showErrorMessage && <ErrorMessage />}

        {!showErrorMessage && (
          <form
            onReset={formik.handleReset}
            onSubmit={formik.handleSubmit}
            className="font-medium ton-paragraph-16"
          >
            <div className="flex flex-col gap-16 mx-auto max-w-7xl">
              <div className="flex flex-col gap-8">
                <label htmlFor="input-name" className="cursor-pointer">
                  Nome Completo
                </label>

                <input
                  required
                  type="text"
                  name="name"
                  id="input-name"
                  value={formik.values.name}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder="Nome e Sobrenome"
                  className={cn(
                    'p-12 font-roboto placeholder:text-display-500 rounded-4 border-display-400 ton-paragraph-16',
                    formik.touched.name &&
                      formik.errors.name &&
                      'placeholder:text-red-500 text-red-500 active:text-red-500 border-red-500 hover:border-red-500 focus:text-red-500 focus:border-red-500 focus:ring-red-500'
                  )}
                />

                {formik.touched.name && formik.errors.name && (
                  <p className="mt-8 text-red-500 paragraph-12">
                    {formik.errors.name}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-8">
                <label htmlFor="input-email" className="cursor-pointer">
                  Email
                </label>

                <input
                  required
                  type="email"
                  name="email"
                  id="input-email"
                  value={formik.values.email}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder="seunome@email.com.br"
                  className={cn(
                    'p-12 font-roboto placeholder:text-display-500 rounded-4 border-display-400 ton-paragraph-16',
                    formik.touched.email &&
                      formik.errors.email &&
                      'placeholder:text-red-500 text-red-500 active:text-red-500 border-red-500 hover:border-red-500 focus:text-red-500 focus:border-red-500 focus:ring-red-500'
                  )}
                />

                {formik.touched.email && formik.errors.email && (
                  <p className="mt-8 text-red-500 paragraph-12">
                    {formik.errors.email}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-8">
                <label htmlFor="input-phone" className="cursor-pointer">
                  Celular
                </label>

                <input
                  required
                  type="text"
                  name="phone"
                  id="input-phone"
                  ref={inputPhoneRef}
                  value={formik.values.phone}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  placeholder="(XX) 99999-9999"
                  className={cn(
                    'p-12 font-roboto placeholder:text-display-500 rounded-4 border-display-400 ton-paragraph-16',
                    formik.touched.phone &&
                      formik.errors.phone &&
                      'placeholder:text-red-500 text-red-500 active:text-red-500 border-red-500 hover:border-red-500 focus:text-red-500 focus:border-red-500 focus:ring-red-500'
                  )}
                />

                {formik.touched.phone && formik.errors.phone && (
                  <p className="mt-8 text-red-500 paragraph-12">
                    {formik.errors.phone}
                  </p>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-24 mt-24 text-center">
              <p className="font-normal text-newTon-900 ton-paragraph-12">
                Ao clicar no botão, você aceita os{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://documentos.ton.com.br/termos-e-condicoes.pdf"
                  className="text-newTon-700 underline"
                >
                  termos e condições
                </a>{' '}
                e declara o conhecimento sobre o{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://documentos.ton.com.br/politica-de-privacidade.pdf"
                  className="text-newTon-700 underline"
                >
                  Aviso de Privacidade do Ton
                </a>
                .
              </p>

              <button
                type="submit"
                disabled={!formik.isValid || loadingEmail}
                className={cn(
                  'py-16 px-40 w-full max-w-[464px] font-bold text-display-900 bg-newTon-400 rounded-32 disabled:cursor-not-allowed ton-paragraph-16',
                  !loadingEmail && 'disabled:bg-display-200'
                )}
              >
                {loadingEmail && (
                  <Icon
                    className={cn(styles.spinner, 'mx-auto')}
                    name="rotating-bars-anim"
                  />
                )}

                {!loadingEmail && 'Abra sua Conta Ton grátis'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  )
}
